import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { UseCaseChat } from '../entities/useCaseChat/UseCaseChat';
import { getUseCaseService } from '../services/UseCaseService';
import { AISystemContext, AIUseCaseSummary, BusinessContext, IntendedUse, UseCase, UseCaseDataContext } from '../entities/useCaseChat/UseCase';
import { ApiRetryableError } from '../services/api';

import { Chat, Message } from '../entities/useCaseChat/Chat';

export interface ChatSendError {
  message: string;
  retryable: boolean;
  retryAfter: number | null;
  errorType: 'network' | 'api';
}

// 502/503/504 mean the proxy or gateway couldn't reach the upstream - treat as network errors.
const GATEWAY_ERROR_CODES = new Set([502, 503, 504]);

function extractChatSendError(error: unknown): ChatSendError {
  if (error instanceof ApiRetryableError) {
    const isNetworkLike = error.statusCode === null || GATEWAY_ERROR_CODES.has(error.statusCode ?? -1);
    const errorType: 'network' | 'api' = isNetworkLike ? 'network' : 'api';
    console.log('[ChatSendError] statusCode:', error.statusCode, '| isNetworkLike:', isNetworkLike, '| errorType:', errorType);
    return {
      message: error.message || 'The request failed. Please try again.',
      retryable: error.retryable,
      retryAfter: error.retryAfter,
      errorType,
    };
  }
  const offlineErrorType: 'network' | 'api' = navigator.onLine ? 'api' : 'network';
  if (error instanceof Error) {
    return { message: error.message || 'The request failed. Please try again.', retryable: false, retryAfter: null, errorType: offlineErrorType };
  }
  return { message: 'An unknown error occurred. Please try again.', retryable: false, retryAfter: null, errorType: offlineErrorType };
}

interface IUseCaseChatState {
  useCaseChat: UseCaseChat;
  useCaseChatIsLoading: boolean;
  waitingForChatResponse: boolean;
  chatSendError: ChatSendError | null;

  setUseCaseChat: (useCaseChat: UseCaseChat) => void;
  clearChatSendError: () => void;

  sendMessage: (messageContent: string) => Promise<void>;

  createUseCaseChat: () => Promise<string>; // returns new chat ID
  getUseCaseChatById: (id: string) => Promise<UseCaseChat>;
  refreshChat: (id: string) => Promise<void>;

  setUseCaseSummary: (summary: Partial<AIUseCaseSummary>) => Promise<UseCaseChat>;
  setIntendedUse: (intendedUse: Partial<IntendedUse>) => Promise<UseCaseChat>;
  setBusinessContext: (businessContext: Partial<BusinessContext>) => Promise<UseCaseChat>;
  setUseCaseDataContext: (useCaseDataContext: Partial<UseCaseDataContext>) => Promise<UseCaseChat>;
  setSystemContext: (systemContext: Partial<AISystemContext>) => Promise<UseCaseChat>;

}


export class UseCaseChatStore {
  static useUseCaseChatStore = create<IUseCaseChatState>()(
    devtools(
      subscribeWithSelector(
        (set, get) => {

          // local helper: update nested UseCase immutably and return updated useCaseChat (to avoid stale closure issues in the async setters)
          const updateUseCase = (updater: (useCase: UseCase) => UseCase, actionName?: string) => {
            let updatedUseCaseChat!: UseCaseChat;

            set(
              (state) => {
                const currentUseCaseChat = state.useCaseChat;
                const nextUseCase = updater(currentUseCaseChat.useCase);

                updatedUseCaseChat = new UseCaseChat({
                  ...currentUseCaseChat,
                  useCase: nextUseCase,
                });

                return { useCaseChat: updatedUseCaseChat };
              },
              false,
              actionName
            );

            return updatedUseCaseChat;
          };

          return {
            useCaseChat: new UseCaseChat(),
            useCaseChatIsLoading: false,
            waitingForChatResponse: false,
            chatSendError: null,

            clearChatSendError: () => {
              set({ chatSendError: null }, false, 'clearChatSendError');
            },

            setUseCaseChat: (useCaseChat: UseCaseChat) => {
              // set(partialStateOrUpdater, replace?: boolean, actionName?: string)   Default: replace = false, which means the new state will be shallow merged with the current state. If set to true, the new state will replace the current state instead of merging.
              set({
                useCaseChat
              }, false, 'setUseCaseChat'
              );
            },

            // Creates a empty object (fetches it into the store) and returns the id of the created chat
            createUseCaseChat: async (): Promise<string> => {
              const useCaseService = getUseCaseService();

              const id = await useCaseService.createUseCase()
                .then(useCase => useCaseService.getUseCaseById(useCase.useCaseChatId))
                .then(useCaseChat => {
                  set(
                    { useCaseChat }, false, 'createUseCaseChat/fulfilled'
                  );
                  return useCaseChat.id;
                }).catch(error => {
                  set({ useCaseChat: new UseCaseChat() }, false, 'createUseCaseChat/rejected'); // reset to default empty chat on failure
                  console.error("Failed to create use case chat:", error);
                  throw new Error("Unable to create use case chat");
                });
              return id;
            },

            sendMessage: async (messageContent: string) => {
              const useCaseService = getUseCaseService();
              const currentChat = get().useCaseChat;
              const chatId = currentChat.id;

              const optimisticMessage = new Message({
                id: `temp-${Date.now()}`,
                messageContent,
                sender: "user",
                sentDate: new Date(),
              });

              const optimisticChat = new UseCaseChat({
                ...currentChat,
                chat: new Chat({
                  ...currentChat.chat,
                  messages: [...currentChat.chat.messages, optimisticMessage],
                }),
              });

              set({ useCaseChat: optimisticChat, waitingForChatResponse: true, chatSendError: null }, false, 'sendMessage/pending');

              let sendErrorState: ChatSendError | null = null;

              try {
                const updatedUseCaseChat = await useCaseService.sendMessage(chatId, messageContent);
                set({ useCaseChat: updatedUseCaseChat }, false, 'sendMessage/fulfilled');
              } catch (error) {
                console.error("Failed to send message:", error);
                set({ useCaseChat: currentChat }, false, 'sendMessage/rollback');
                try {
                  const latestChat = await useCaseService.getUseCaseById(chatId);
                  set({ useCaseChat: latestChat }, false, 'sendMessage/recovery');
                } catch (refetchError) {
                  console.error("Recovery refetch also failed:", refetchError);
                }
                sendErrorState = extractChatSendError(error);
                throw error;
              } finally {
                // Atomically clear loading and surface any error - Thinking... never disappears silently
                set({ waitingForChatResponse: false, chatSendError: sendErrorState }, false, 'sendMessage/finally');
              }
            },

            getUseCaseChatById: async (id: string): Promise<UseCaseChat> => {

              set({ useCaseChatIsLoading: true }, false, 'getUseCaseChatById/pending');

              try {
                const useCaseService = getUseCaseService();
                const useCaseChat = await useCaseService.getUseCaseById(id);
                set({ useCaseChat }, false, 'getUseCaseChatById/fulfilled');
                return useCaseChat;
              } catch (error) {
                console.error("Failed to fetch use case chat:", error);
                throw error;
              } finally {
                set({ useCaseChatIsLoading: false }, false, 'getUseCaseChatById/finally');
              }
            },
            refreshChat: async (id: string): Promise<void> => {
              try {
                const useCaseService = getUseCaseService();
                const useCaseChat = await useCaseService.getUseCaseById(id);
                set({ useCaseChat }, false, 'refreshChat/fulfilled');
              } catch (error) {
                console.error("Failed to refresh chat:", error);
              }
            },

            //===============================================================================================
            //TODO: implement partial patch with API
            setUseCaseSummary: async (summary: Partial<AIUseCaseSummary>): Promise<UseCaseChat> => {

              return updateUseCase((useCase) => {
                const updatedSummary = new AIUseCaseSummary({
                  ...useCase.useCaseSummary,
                  ...summary,
                });

                return new UseCase({
                  ...useCase,
                  useCaseSummary: updatedSummary,
                });
              }, 'setUseCaseSummary');
            },

            setIntendedUse: async (intendedUse: Partial<IntendedUse>): Promise<UseCaseChat> => {
              return updateUseCase((useCase) => {
                const updatedIntendedUse = new IntendedUse({
                  ...useCase.intendedUse,
                  ...intendedUse,
                });

                return new UseCase({
                  ...useCase,
                  intendedUse: updatedIntendedUse,
                });
              }, 'setIntendedUse');
            },

            setBusinessContext: async (businessContext: Partial<BusinessContext>): Promise<UseCaseChat> => {
              return updateUseCase((useCase) => {
                const updatedBusinessContext = new BusinessContext({
                  ...useCase.businessContext,
                  ...businessContext,
                });

                return new UseCase({
                  ...useCase,
                  businessContext: updatedBusinessContext,
                });
              }, 'setBusinessContext');
            },

            setUseCaseDataContext: async (useCaseDataContext: Partial<UseCaseDataContext>): Promise<UseCaseChat> => {
              return updateUseCase((useCase) => {
                const updatedUseCaseDataContext = new UseCaseDataContext({
                  ...useCase.dataContext,
                  ...useCaseDataContext,
                });

                return new UseCase({
                  ...useCase,
                  dataContext: updatedUseCaseDataContext,
                });
              }, 'setUseCaseDataContext');
            },

            setSystemContext: async (systemContext: Partial<AISystemContext>): Promise<UseCaseChat> => {
              return updateUseCase((useCase) => {
                const updatedSystemContext = new AISystemContext({
                  ...useCase.systemComponents,
                  ...systemContext,
                });

                return new UseCase({
                  ...useCase,
                  systemComponents: updatedSystemContext,
                });
              }, 'setSystemContext');
            },

          } // returned object
        } // (set,get)
      ),// subscribeWithSelector
      { name: 'UseCaseChatStore' }
    )// devtools
  )// create useUseCaseChatStore
}// end class UseCaseChatStore


export default UseCaseChatStore;


